import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { FaTrash, FaImage } from 'react-icons/fa';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';
import DeleteModal from "../../components/modals/DeleteModal";
import SuccessModal from "../../components/modals/SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import { Modal, Button } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import '../../styles/AddBanners.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Banner image validation function (16:9 aspect ratio for web banners)
const validateBannerImage = (file) => {
  return new Promise((resolve) => {
    // Check file size first (5MB limit for banners)
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    
    if (file.size > maxSizeInBytes) {
      resolve({
        isValid: false,
        error: `File size too large (${fileSizeInMB}MB). Maximum allowed: 5MB`,
        fileName: file.name,
        fileSize: fileSizeInMB
      });
      return;
    }

    const img = new Image();
    img.onload = () => {
      // Banner images should have 16:9 aspect ratio (width:height = 16:9)
      const actualRatio = img.width / img.height;
      const expectedRatio = 16 / 9; // 1.778
      const tolerance = 0.15; // Allow 15% tolerance for banners
      
      const isValidRatio = Math.abs(actualRatio - expectedRatio) <= tolerance;
      
      // Also check minimum dimensions for banners
      const minWidth = 640;
      const minHeight = 360;
      const isValidSize = img.width >= minWidth && img.height >= minHeight;
      
      resolve({
        isValid: isValidRatio && isValidSize,
        actualRatio: actualRatio,
        expectedRatio: expectedRatio,
        width: img.width,
        height: img.height,
        fileName: file.name,
        fileSize: fileSizeInMB,
        sizeError: !isValidSize ? `Minimum size required: ${minWidth}x${minHeight}px` : null,
        ratioError: !isValidRatio ? `Invalid aspect ratio (${actualRatio.toFixed(2)}:1). Required: 16:9 ratio` : null
      });
    };
    img.onerror = () => {
      resolve({
        isValid: false,
        error: 'Failed to load image',
        fileName: file.name,
        fileSize: fileSizeInMB
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

export default function AddBanners() {
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;

  // Notification modal hook
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  // State management
  const [mainCategories, setMainCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isValidatingImage, setIsValidatingImage] = useState(false);
  const [imageValidationError, setImageValidationError] = useState("");
  const [editingBanner, setEditingBanner] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBannerId, setDeleteBannerId] = useState(null);

  // Crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [tempImageUrl, setTempImageUrl] = useState("");

  // Fetch main categories
  const fetchMainCategories = useCallback(async () => {
    if (!company_code) return;

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/categories/get-categories`,
        {
          params: { company_code },
        }
      );

      if (response.data.success) {
        // Filter only main categories (no parent_id)
        const mainCats = response.data.categories.filter(
          (cat) => !cat.parent_id
        );
        setMainCategories(mainCats);
      }
    } catch (err) {
      showError("Error fetching categories", "Error");
      console.error("Error fetching categories:", err);
    }
  }, [company_code, showError]);

  // Fetch banners
  const fetchBanners = useCallback(async () => {
    if (!company_code) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/banners/get-banners`,
        {
          params: { company_code },
        }
      );

      if (response.data.success) {
        setBanners(response.data.banners);
      }
    } catch (err) {
      showError("Error fetching banners", "Error");
      console.error("Error fetching banners:", err);
    } finally {
      setLoading(false);
    }
  }, [company_code, showError]);

  // Load data on component mount
  useEffect(() => {
    fetchMainCategories();
    fetchBanners();
  }, [fetchMainCategories, fetchBanners]);

  // Handle image file selection - open crop modal
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl("");
      setImageValidationError("");
      return;
    }

    // Basic file type and size validation
    if (!file.type.startsWith('image/')) {
      setImageValidationError("Please select a valid image file");
      e.target.value = "";
      return;
    }

    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeInBytes) {
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      setImageValidationError(`File size too large (${fileSizeInMB}MB). Maximum allowed: 5MB`);
      e.target.value = "";
      return;
    }

    // Store the file temporarily and show crop modal
    setTempImageFile(file);
    setTempImageUrl(URL.createObjectURL(file));
    setShowCropModal(true);
    setImageValidationError("");
  };

  // Handle crop completion
  const handleCropComplete = async (croppedFile) => {
    setShowCropModal(false);
    setIsValidatingImage(true);

    try {
      const validationResult = await validateBannerImage(croppedFile);

      if (!validationResult.isValid) {
        const errorMessage =
          validationResult.error ||
          [validationResult.sizeError, validationResult.ratioError]
            .filter(Boolean)
            .join(" and ");

        setImageValidationError(
          `${validationResult.fileName} (${validationResult.fileSize}MB): ${errorMessage}`
        );
        setSelectedFile(null);
        setPreviewUrl("");
      } else {
        setSelectedFile(croppedFile);
        setPreviewUrl(URL.createObjectURL(croppedFile));
        setImageValidationError("");
      }
    } catch (error) {
      setImageValidationError("Error validating image. Please try again.");
      setSelectedFile(null);
      setPreviewUrl("");
    }

    setIsValidatingImage(false);
    
    // Clean up temp image
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
    }
    setTempImageFile(null);
    setTempImageUrl("");
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropModal(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
    }
    setTempImageFile(null);
    setTempImageUrl("");
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCategory || !selectedFile) {
      showError("Please select a category and upload a banner image", "Validation Error");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("company_code", company_code);
      formData.append("category_id", selectedCategory);
      formData.append("banner_image", selectedFile);

      const url = editingBanner
        ? `${BASE_URL}/api/admin/banners/update-banner/${editingBanner.banner_id}`
        : `${BASE_URL}/api/admin/banners/add-banner`;

      const method = editingBanner ? "put" : "post";

      const response = await axios[method](url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        showSuccess(
          editingBanner
            ? "Banner updated successfully!"
            : "Banner added successfully!",
          "Success"
        );
        setSelectedCategory("");
        setSelectedFile(null);
        setPreviewUrl("");
        setEditingBanner(null);
        fetchBanners();

        // Clear form
        document.getElementById("bannerForm").reset();
      }
    } catch (err) {
      showError(
        err.response?.data?.message || "Error saving banner",
        "Error"
      );
      console.error("Error saving banner:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = (bannerId) => {
    setDeleteBannerId(bannerId);
    setShowDeleteModal(true);
  };

  // Handle delete success
  const handleDeleteSuccess = () => {
    showSuccess("Banner deleted successfully!", "Success");
    fetchBanners();
  };

  // Handle delete error
  const handleDeleteError = (error) => {
    showError(error?.message || "Error deleting banner", "Error");
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingBanner(null);
    setSelectedCategory("");
    setSelectedFile(null);
    setPreviewUrl("");
    setImageValidationError("");
    document.getElementById("bannerForm").reset();
  };

  // Group banners by category
  const bannersByCategory = banners.reduce((acc, banner) => {
    let categoryName;
    if (
      banner.category_id === null ||
      banner.category_id === undefined ||
      banner.category_name === "Home Page"
    ) {
      categoryName = "Home Page";
    } else {
      categoryName = banner.category_name || "Unknown Category";
    }

    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(banner);
    return acc;
  }, {});

  // Helper function to create a crop area with aspect ratio 16:9 (width:height)
  function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  // Image Crop Modal Component
  const ImageCropModal = ({
    show,
    onHide,
    imageSrc,
    onCropComplete,
    fileName,
  }) => {
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const aspect = 16 / 9; // width:height = 16:9 for banners

    function onImageLoad(e) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }

    const handleCropConfirm = async () => {
      if (!completedCrop || !imgRef.current || !previewCanvasRef.current) {
        return;
      }

      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const crop = completedCrop;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calculate the actual crop dimensions in pixels
      const cropWidth = crop.width * scaleX;
      const cropHeight = crop.height * scaleY;

      // Set minimum dimensions (640x360 for 16:9 ratio)
      const minWidth = 640;
      const minHeight = 360;
      
      // Calculate output dimensions - ensure minimum size is met
      let outputWidth = Math.max(cropWidth, minWidth);
      let outputHeight = Math.max(cropHeight, minHeight);
      
      // Maintain 16:9 aspect ratio
      const targetRatio = 16 / 9;
      const currentRatio = outputWidth / outputHeight;
      
      if (Math.abs(currentRatio - targetRatio) > 0.01) {
        // Adjust dimensions to maintain exact 16:9 ratio
        if (currentRatio > targetRatio) {
          outputHeight = outputWidth / targetRatio;
        } else {
          outputWidth = outputHeight * targetRatio;
        }
      }

      // Round to whole numbers
      outputWidth = Math.round(outputWidth);
      outputHeight = Math.round(outputHeight);

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext("2d");
      
      // Enable image smoothing for better quality when scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const croppedFile = new File([blob], fileName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            onCropComplete(croppedFile);
          }
        },
        "image/jpeg",
        0.95
      );
    };

    return (
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="crop-modal-dialog"
        contentClassName="crop-modal-content"
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Banner Image to 16:9 Ratio</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "hidden" }}>
          <div className="crop-instructions">
            <small className="text-muted">
              Adjust the crop area to select the portion of your image. The final image will be 16:9 aspect ratio (width:height).
            </small>
          </div>
          <div
            style={{
              maxHeight: "80vh",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                style={{ maxWidth: "100%", width: "fit-content" }}
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={imageSrc}
                  onLoad={onImageLoad}
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "70vh",
                    width: "auto",
                    height: "auto",
                    display: "block",
                  }}
                />
              </ReactCrop>
            )}
          </div>
          <canvas ref={previewCanvasRef} style={{ display: "none" }} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCropConfirm}>
            Apply Crop
          </Button>
        </Modal.Footer>
        <style jsx="true">{`
          .crop-modal-dialog {
            max-width: fit-content !important;
            width: auto !important;
            margin: 1.75rem auto;
          }
          .crop-modal-content {
            width: fit-content !important;
          }
          .crop-instructions {
            margin-bottom: 15px;
            padding: 10px;
            background-color: #f8f9fa;
            border-radius: 4px;
            text-align: center;
          }
        `}</style>
      </Modal>
    );
  };

  return (
    <div className="add-banners-container">
      <div className="category-header-section">
        <h1>Banner Management</h1>
        <p className="category-subtitle">
          Upload banner images for main categories on your website
        </p>
      </div>

      {/* Banner Upload Form */}
      <div className="banner-form-container">
        <div className="form-card">
          <h2>{editingBanner ? "Edit Banner" : "Add New Banner"}</h2>

          <form id="bannerForm" onSubmit={handleSubmit} className="banner-form">
            <div className="form-group">
              <label className="form-label">Main Category *</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select a main category</option>
                <option value="0">Home Page</option>
                {mainCategories.map((category) => (
                  <option
                    key={category.category_id}
                    value={category.category_id}
                  >
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Banner Image *</label>
              <div className="image-upload-note">
                <small className="text-muted">
                  🖼️ Please upload banner images with 16:9 aspect ratio (e.g.,
                  1920x1080px, 1600x900px) and minimum size of 640x360px.
                  Maximum file size: 5MB
                </small>
              </div>
              <input
                type="file"
                className="form-input"
                accept="image/*"
                onChange={handleImageChange}
                disabled={isValidatingImage}
                required={!editingBanner}
              />

              {isValidatingImage && (
                <div className="validation-message validating">
                  Validating banner image...
                </div>
              )}

              {imageValidationError && (
                <div className="validation-message error">
                  {imageValidationError}
                </div>
              )}

              {selectedFile && !imageValidationError && !isValidatingImage && (
                <div className="validation-message success">
                  ✓ Banner image validated successfully
                </div>
              )}
            </div>

            {/* Image Preview */}
            {previewUrl && (
              <div className="image-preview">
                <label className="form-label">Preview:</label>
                <img
                  src={previewUrl}
                  alt="Banner Preview"
                  className="banner-preview"
                />
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              {editingBanner && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading ||
                  isValidatingImage ||
                  !selectedCategory ||
                  (!selectedFile && !editingBanner)
                }
              >
                {loading
                  ? "Saving..."
                  : editingBanner
                  ? "Update Banner"
                  : "Add Banner"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Existing Banners */}
      <div className="banners-list-container">
        <h2>Existing Banners</h2>

        {loading ? (
          <Spinner text="Loading banners..." />
        ) : Object.keys(bannersByCategory).length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-content">
            <FaImage size={50} />
            <h3>No Banners Found</h3>
            <p>Upload your first banner image above</p>
          </div>
          </div>
        ) : (
          <div className="banners-grid">
            {Object.entries(bannersByCategory).map(
              ([categoryName, categoryBanners]) => (
                <div key={categoryName} className="category-section">
                  <h3 className="category-title">{categoryName}</h3>
                  <div className="banners-row">
                    {categoryBanners.map((banner) => (
                      <div key={banner.banner_id} className="banner-card">
                        <div className="banner-image-container">
                          <img
                            src={`${BASE_URL}/uploads/banners/${banner.banner_url}`}
                            alt={`Banner for ${categoryName}`}
                            className="banner-image"
                          />
                          <div className="banner-overlay">
                            <button
                              className="btn btn-delete"
                              onClick={() =>
                                handleDeleteClick(banner.banner_id)
                              }
                              title="Delete Banner"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                        <div className="banner-info">
                          <p className="banner-date">
                            Added:{" "}
                            {new Date(banner.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        id={deleteBannerId}
        deleteUrl={`/api/admin/banners/delete-banner/:id`}
        entityLabel="banner"
        modalTitle="Delete Banner"
        confirmMessage="Are you sure you want to delete this banner? This action cannot be undone."
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteError={handleDeleteError}
        requestOptions={{
          params: { company_code },
        }}
      />

      {/* Crop Modal */}
      {showCropModal && (
        <ImageCropModal
          show={showCropModal}
          onHide={handleCropCancel}
          imageSrc={tempImageUrl}
          onCropComplete={handleCropComplete}
          fileName={tempImageFile?.name || "banner.jpg"}
        />
      )}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        title={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        title={errorTitle}
        message={errorMessage}
      />
    </div>
  );
}
